import { PageEdge, PageType } from '../../../types/graphql';
import { decodeNodeId, isImageMedia } from '../../utils';
import {
  fetchFundraisingPagesRequestError,
  fetchFundraisingPagesRequestMade,
  fetchFundraisingPagesRequestSuccess,
} from '../actionCreators/apiActions';

import { Action } from 'redux';
import { Auth } from '@justgiving/auth';
import { ERROR_MESSAGES } from '../../consts';
import { IAppState } from '../../types/state.d';
import { ThunkDispatch } from 'redux-thunk';
import apolloClient from '../../apolloClient';
import config from '../../config';
import { gql } from '@apollo/client';
import logInfo from './logInfo';

export type PageListItem = {
  fundraiserPageGuid: string;
  heroImageName: string;
  pageTitle: string;
  pageType: PageType;
};

const fetchFundraisingPages = (teamGuid?: string) => async (
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
  _state: () => IAppState,
  auth: Auth,
) => {
  try {
    await auth.refreshAccessTokenIfExpired();

    dispatch(fetchFundraisingPagesRequestMade());

    const { data, error } = await apolloClient.query({
      query: config.features.useESEligibleChildPages
        ? GET_ELIGIBLE_CHILD_PAGES_QUERY
        : GET_PAGES_QUERY,
      variables: { teamGuid: teamGuid },
    });

    if (error) {
      throw new Error(ERROR_MESSAGES.GET_FUNDRAISING_PAGES);
    }

    const pagesMappedToPageList = data.viewer.eligibleChildPages.edges.map(
      toPageList,
    );

    dispatch(fetchFundraisingPagesRequestSuccess(pagesMappedToPageList));
  } catch (error) {
    const logData = {
      message: ERROR_MESSAGES.GET_FUNDRAISING_PAGES,
      method: 'GET',
      teamGuid,
      errorMessage: (error as Error).message,
    };
    dispatch(logInfo(logData));
    dispatch(fetchFundraisingPagesRequestError((error as Error).message));
  }
};

export default fetchFundraisingPages;

const toPageList = ({ node }: PageEdge): PageListItem => {
  const params =
    node &&
    node.cover &&
    isImageMedia(node.cover) &&
    node.cover.url &&
    node.cover.url.match(/.*\/(.*)\/(.*)$/);

  const trimmedImageUrl = params && params[2];

  const url =
    params && trimmedImageUrl
      ? `${trimmedImageUrl}?template=FundraisingPageHeroImageM`
      : config.defaultCoverPhoto;

  const decodedNodeId = decodeNodeId(node.id);

  return {
    fundraiserPageGuid: decodedNodeId.parts[1],
    heroImageName: url,
    pageTitle: node.title,
    pageType:
      node.product?.name === 'fundraising'
        ? PageType.OnePage
        : PageType.Fundraising,
  };
};

const GET_PAGES_QUERY = gql`
  query GetPages($teamGuid: ID!) {
    viewer {
      eligibleChildPages: pages(
        first: 10
        canParticipateIn: { teamId: $teamGuid }
      ) {
        edges {
          node {
            type
            slug
            id
            title
            product {
              name
            }
            cover {
              ... on ImageMedia {
                id
                url
                alt
                caption
              }
            }
          }
        }
      }
    }
  }
`;

const GET_ELIGIBLE_CHILD_PAGES_QUERY = gql`
  query GetEligibleChildPages($teamGuid: UUID!) {
    viewer {
      eligibleChildPages(first: 10, parentPageId: $teamGuid) {
        edges {
          node {
            type
            slug
            id
            title
            product {
              name
            }
            cover {
              ... on ImageMedia {
                id
                url
                alt
                caption
              }
            }
          }
        }
      }
    }
  }
`;
